import {Component, ElementRef, Input, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {GlobalService} from 'app/global/global.service';
import {SharedStoreFacade} from 'app/main/shared/shared/store/shared-store.facade';
import {ActivityLogsUtils} from 'app/main/shared/styles/activity-logs-utils';
import {ChoiceWithIndices} from 'ngx-mentions';
import {delay, of, Subscription, take} from 'rxjs';
import {User} from '../../../pages/contract-control/contract-control-interfaces/cc-activity.interface';
import {ActivityLogService} from '../../signing-sidebar/activity-logs/activity-logs-service';
import { PermissionsService } from 'app/global/permissions/permissions.service';
import { PermissionsEnum } from 'app/global/permissions/permissions.enum';
import { TemplateDetails } from 'app/main/pages/documents/dto/document-details.dto';
import { TranslocoService } from '@jsverse/transloco';

export enum ChatScopEnum {
  Public = 'public',
  Internal = 'internal'
}

@Component({
  selector: 'app-activity-form',
  templateUrl: './activity-form.component.html',
  styleUrl: './activity-form.component.scss',
  encapsulation: ViewEncapsulation.None
})


export class ActivityFormComponent implements OnInit {
  
  @ViewChild('attachmentElement') attachmentElement: ElementRef;

  @Input() documentDetails$: TemplateDetails;
  @Input() roleId: number = 0;
  @Input() documentId: number = 0;
  @Input() hasViewAccess: boolean = false;
  @Input() view: string = 'signingArea';

  public loading = false;
  public users: User[] = [];
  public timeOutSubscription: Subscription;
  public mentions: ChoiceWithIndices[] = [];
  public searchRegexp = new RegExp('^([-&.\\w]+ *){0,3}$');
  public selectedChoices: ChoiceWithIndices[] = [];
  public readonly ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif'];
  public readonly acceptedImageTypes = this.ACCEPTED_IMAGE_TYPES.join(',');
  public mentionsConfig = [
    {
      triggerCharacter: '@',
      getChoiceLabel: (user): string => {
        return `@${user.name}`;
      },
    }
  ];

  public canAccessChat$ = of(false);
  public chatForm: FormGroup;
  public sendMessageLoader: boolean = false;
  public submitted: boolean = false;
  public chatScopes = [
    {name: 'Internal comment', value: ChatScopEnum.Internal},
    {name: 'Public comment', value: ChatScopEnum.Public}
  ]
  public sendInternalChatFormLoader$ = this._sharedStoreFacade$.sendInternalChatFormLoader$;
  constructor(
    private _formBuilder: FormBuilder, 
    private activityLogService: ActivityLogService,
    private _sharedStoreFacade$: SharedStoreFacade,
    private activityLogsUtils: ActivityLogsUtils,
    private _globalService$: GlobalService,
    private _permissionService: PermissionsService,
    private _translocoService: TranslocoService
  ) {}

  ngOnInit(): void {
    this.initChatForm();
    if(this.view === 'documentArea' || this.view === 'senderArea'){
      this.canAccessChat$ = this._permissionService.isNotRestricted(PermissionsEnum.restrict_access_activate_or_use_chat);
      this._sharedStoreFacade$.fetchSubUsers();
    }
    if(!this.documentDetails$?.external_chat || this.hasViewAccess){
      this.chatScopes.splice(1, 1);
    }
  }


  /**
   * On selected Choice
   * @param choices 
   */
  onSelectedChoicesChange(choices: ChoiceWithIndices[]): void {
    this.mentions = choices;
    this.form.tags.reset();
    if (choices.length) {
      this.form.tags.setValue(choices.map(item => item.choice.id));
    }
  }




  get form(){
    return this.chatForm.controls;
  }

  
  
  /**
   * Initializes the chat form with default values and validators.
   * 
   * The form contains the following controls:
   * - `message`: A required field with a minimum length of 1 character.
   * - `attachment`: An optional field for file attachments.
   * - `scope`: Determines the visibility of the chat message. It is set to `Public` if internal chat is active and the user does not have view access; otherwise, it is set to `Internal`.
   * - `tags`: An optional field for tagging the message.
   * 
   * The `isExternalChatActive` variable checks if external chat is allowed based on the `documentDetails$` property.
   */
  initChatForm(): void {
    const isExternalChatActive = !!this.documentDetails$?.external_chat;
    this.chatForm = this._formBuilder.group({
      message: [null, [
        Validators.required,
        Validators.minLength(1)
      ]],
      attachment: [null],
      scope: [isExternalChatActive && this.hasViewAccess === false ? ChatScopEnum.Public : ChatScopEnum.Internal],
      tags: [[]],
    });
  }


  
  async loadUsers($event){
    this.users = await this.activityLogsUtils.loadUsers($event);
  }


  
  /**
   * Message control getter
   */
  get message() {
    return this.chatForm.get('message');
  }

  
  /**
   * On Adding Attachment in Chat
   * @param event 
   */
  onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input || !input.files?.length) return;
    const file = input.files?.[0];

    if (!file) return;

    if (!this.ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      this._globalService$.toastrError(this._translocoService.translate('File type error (document activity chat) -  File type is not supported'));
      input.value = '';
      return;
    }

    this.chatForm.patchValue({
      attachment: file
    });
    this.chatForm.get('attachment').updateValueAndValidity();

    if (this.view === 'signingArea') {
      this.sendMessage();
    } else {
      this.sendInternalMessage();  
    }
    input.value = '';
  }



  /**
   * Get Display Label
   * @param item 
   * @returns 
   */
  getDisplayLabel = (item: {name:string}): string => {
    return item.name;
  };

  /**
   * Form submit handler
   */
  onSubmit() {
    this.submitted = true;
    if (this.chatForm.invalid) return;
    this.sendMessage();
  }

  selectChatScope(scope: ChatScopEnum){
    this.chatForm.get('scope').setValue(scope);
  }

  sendInternalMessage(){
    if(!this.validateChatForm()){
      return;
    }  
    let message = this.chatForm.get('message')?.value;
    this.mentions.forEach(item => message = message.replaceAll(''+item.choice.name+'', '{'+item.choice.id+'}'));
    const formData: FormData = new FormData();
    formData.append("message", message || '');
    formData.append("scope", this.chatForm.get('scope')?.value || '');
    const tags = this.chatForm.get('tags')?.value || [];
    tags.forEach(tag => {
      formData.append('tags[]', tag);
    });
    if (this.chatForm.get('attachment').value) {
      formData.append("attachment", this.chatForm.get('attachment')?.value);
    }
    this._sharedStoreFacade$.sendInternalMessage(this.documentId, formData);
    this.timeOutSubscription = of(null).pipe(
      delay(500),
      take(1)
    ).subscribe(() => {
      const currentScope = this.form.scope.value;
      this.chatForm.reset();
      this.chatForm.patchValue({scope: currentScope});
      this.mentions = [];
    })
  }

  sendMessage() {
    if(!this.validateChatForm()){
      return;
    }
    this.sendMessageLoader = true;
    this.submitted = false;
    const formData: FormData = new FormData();
    formData.append("message", this.chatForm.get('message')?.value || '');
    if (this.chatForm.get('attachment').value) {
      formData.append("attachment", this.chatForm.get('attachment')?.value);
    }
    this.activityLogService.sendMessage(this.roleId, formData).subscribe({
      next: (chat) => {
        this.sendMessageLoader = false;
        this.chatForm.reset();
        this.attachmentElement.nativeElement.value = null;
        this._sharedStoreFacade$.updateMessage(chat);
      },
      error: () => {
        this.sendMessageLoader = false;
      }
    })
  }

validateChatForm(){
    const message = this.chatForm.get('message')?.value || '';
    return message.trim() !== '' || !!this.chatForm.get('attachment').value;  
  }

}
